import { useState } from "react";
import { Sparkles, Smartphone, Monitor, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { YoutubePublishOptions } from "./YoutubePublishOptions";

const PRESETS = [
  { key: "neem-karoli", label: "Neem Karoli Baba Miracles", testid: "preset-chip-neem-karoli-baba" },
  { key: "cosmic-facts", label: "5 Mind-Blowing Cosmic Facts", testid: "preset-chip-mystery-facts" },
  { key: "ancient-wisdom", label: "Ancient Wisdom Quotes", testid: "preset-chip-ancient-wisdom" },
];

const DURATIONS = [
  { key: "short", label: "Short (~1 min)", testid: "duration-short-button" },
  { key: "medium", label: "Medium (~6 min)", testid: "duration-medium-button" },
  { key: "long", label: "Long (~12 min)", testid: "duration-long-button" },
];

const SPEEDS = ["1.0", "1.1", "1.2", "1.3", "1.5", "custom"];

export const QUALITIES = [
  { key: "720p", label: "720p", hint: "HD · ~2x faster render", testid: "quality-720p-button" },
  { key: "1080p", label: "1080p", hint: "Full HD · slower render", testid: "quality-1080p-button" },
];

export const GeneratorForm = ({ onCreated }) => {
  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState("hi");
  const [orientation, setOrientation] = useState("shorts");
  const [voiceGender, setVoiceGender] = useState("male");
  const [duration, setDuration] = useState("short");
  const [quality, setQuality] = useState("720p");
  const [speed, setSpeed] = useState("1.0");
  const [customSpeed, setCustomSpeed] = useState("1.4");
  const [submitting, setSubmitting] = useState(false);
  const [publishOpts, setPublishOpts] = useState({ auto_publish: false, publish_privacy: "private", publish_at: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }
    const voiceSpeed = speed === "custom" ? parseFloat(customSpeed) || 1.0 : parseFloat(speed);
    setSubmitting(true);
    try {
      const res = await api.post("/projects", {
        topic: topic.trim(),
        language,
        orientation,
        voice_gender: voiceGender,
        voice_speed: voiceSpeed,
        duration,
        quality,
        auto_publish: publishOpts.auto_publish,
        publish_privacy: publishOpts.publish_privacy,
        publish_at:
          publishOpts.publish_privacy === "schedule" && publishOpts.publish_at
            ? new Date(publishOpts.publish_at).toISOString()
            : null,
      });
      onCreated(res.data.id);
    } catch (e) {
      toast.error("Failed to start video generation");
      setSubmitting(false);
    }
  };

  return (
    <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-amber-500/40 via-slate-800 to-slate-800">
      <div
        className="rounded-2xl bg-slate-900/80 border border-slate-800/80 p-6 sm:p-8 backdrop-blur-md"
        style={{ backgroundImage: "radial-gradient(circle at 50% -20%, rgba(245, 158, 11, 0.12), transparent 70%)" }}
      >
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 mb-1">
          What's trending today?
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          Type any topic — script, voiceover, footage and captions are generated automatically.
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={p.testid}
              onClick={() => setTopic(p.label)}
              className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/60 px-3 py-1.5 rounded-full text-xs transition-all"
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5">
          <input
            data-testid="generator-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Neem Karoli Baba, Ancient Egypt Mysteries, AI News Today..."
            className="w-full bg-slate-950/80 border border-slate-800 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-slate-100 placeholder-slate-500 rounded-xl p-4 text-base sm:text-lg transition-all outline-none"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Language</label>
              <select
                data-testid="generator-language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-500 outline-none"
              >
                <option value="hi">Hindi</option>
                <option value="en">English</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Voice</label>
              <select
                data-testid="voiceover-tts-select"
                value={voiceGender}
                onChange={(e) => setVoiceGender(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-500 outline-none"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Speed</label>
              <select
                data-testid="voice-speed-select"
                value={speed}
                onChange={(e) => setSpeed(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 rounded-xl px-4 py-2.5 focus:border-amber-500 outline-none"
              >
                {SPEEDS.map((s) => (
                  <option key={s} value={s}>
                    {s === "custom" ? "Custom" : s === "1.0" ? "Normal (1.0x)" : `${s}x`}
                  </option>
                ))}
              </select>
              {speed === "custom" && (
                <input
                  data-testid="voice-speed-custom-input"
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="2.5"
                  value={customSpeed}
                  onChange={(e) => setCustomSpeed(e.target.value)}
                  placeholder="e.g. 1.4"
                  className="w-full mt-2 bg-slate-950/80 border border-slate-800 focus:border-amber-500 text-slate-100 rounded-xl px-4 py-2 text-sm outline-none"
                />
              )}
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Orientation</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="orientation-shorts-button"
                  onClick={() => setOrientation("shorts")}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm border transition-all ${
                    orientation === "shorts"
                      ? "bg-amber-500 text-black border-amber-500"
                      : "bg-slate-950/80 border-slate-800 text-slate-300"
                  }`}
                >
                  <Smartphone className="w-4 h-4" /> Shorts
                </button>
                <button
                  type="button"
                  data-testid="orientation-landscape-button"
                  onClick={() => setOrientation("landscape")}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm border transition-all ${
                    orientation === "landscape"
                      ? "bg-amber-500 text-black border-amber-500"
                      : "bg-slate-950/80 border-slate-800 text-slate-300"
                  }`}
                >
                  <Monitor className="w-4 h-4" /> Landscape
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Length</label>
              <div className="flex gap-1.5">
                {DURATIONS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    data-testid={d.testid}
                    onClick={() => setDuration(d.key)}
                    title={d.label}
                    className={`flex-1 rounded-xl px-2 py-2.5 text-xs border transition-all ${
                      duration === d.key
                        ? "bg-amber-500 text-black border-amber-500"
                        : "bg-slate-950/80 border-slate-800 text-slate-300"
                    }`}
                  >
                    {d.label.split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 block">Quality</label>
              <div className="flex gap-1.5">
                {QUALITIES.map((q) => (
                  <button
                    key={q.key}
                    type="button"
                    data-testid={q.testid}
                    onClick={() => setQuality(q.key)}
                    title={q.hint}
                    className={`flex-1 rounded-xl px-2 py-2.5 text-xs border transition-all ${
                      quality === q.key
                        ? "bg-amber-500 text-black border-amber-500"
                        : "bg-slate-950/80 border-slate-800 text-slate-300"
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <YoutubePublishOptions kind="video" slot="video" value={publishOpts} onChange={setPublishOpts} testidPrefix="video-publish" />

          <button
            data-testid="generate-video-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold px-6 py-4 rounded-xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 text-base"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {submitting ? "Starting generation..." : "Generate Video"}
          </button>
        </form>
      </div>
    </div>
  );
};
